// The Client questions tab (ADR-0035): every question to the client with its status (one chip, synced with the portal), batch, area, why it
// must be asked and what it unblocks; and each question's page — the words the client reads, who did what and when, "Why we must ask"
// (the evidence, the rule, what stays unaddressed if it is not asked, and the preparer's words), what it unblocks or may make necessary,
// how the client might answer (scenarios with our moves and their impact), the answers and the history, with the acts the engine offers.
// Deferring, skipping or removing a question is a person's act: the decision dialog shows the proof and what stays unaddressed before it
// asks for the reason. Everything shown is published by the engine (completion key "client_questions"); the UI decides nothing.
import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { ActionIntent } from "../../app/ActionDrawer";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { Button, Card, Drawer, EmptyState, ErrorState, IssueStatusChip, LoadingRows, PageHeader, QuestionStateCount, QuestionStatusChip, StatusPill } from "../../components";
import { newIdempotencyKey } from "../../data/source";
import type { DocModel } from "../../data/types";
import { mayRequest } from "../../lib/commands";
import { fmtDate, fmtStamp } from "../../lib/format";
import { useLoad, usePoll, useRealtime } from "../../lib/hooks";
import { issueHref } from "../../lib/issues";
import {
  QUESTION_FILTERS, QUESTION_STATE, filterQuestions, questionHref, questionsDoc, unblockRefs,
  type PortalCandidate, type QFact, type QProof, type QScenario, type QSegment, type Question, type QuestionsDoc,
} from "../../lib/questions";
import { Who, useWs, useWsAct } from "./common";
import { SourceList } from "./Issues";

/** The published register, reloaded on any workspace change, a finished act, and by short polling (as the issue pages). */
function useQuestions(engagementId: string) {
  const { ds } = useApp();
  const r = useLoad(() => ds.table(engagementId, "completion").catch(() => [] as DocModel[]), [ds, engagementId]);
  useRealtime(ds, "workspace", () => r.reload());
  useRealtime(ds, "inbox", (e) => { if (e.row?.status === "done") r.reload(); });
  usePoll(ds, r.reload);
  return { ...r, parsed: r.data ? questionsDoc(r.data) : null };
}

/** Words the engine rendered from placeholders: each bound figure highlighted with its citation (and what it carries now when it changed). */
function Words({ segs }: { segs: QSegment[] }) {
  return (
    <>
      {segs.map((s, n) => s.t !== undefined ? <span key={n}>{s.t}</span> : (
        <span key={n} data-cite={s.cite ?? undefined} title={`${s.cite ?? s.p}${s.changed ? ` — now ${s.now ?? "does not resolve"}` : ""}`}
          className={`rounded px-0.5 font-medium ${s.changed ? "bg-stale-bg text-stale" : "bg-brand/10"}`}>
          {s.text}{s.changed && <span className="ml-1 text-[11px]">(now {s.now ?? "—"})</span>}
        </span>
      ))}
    </>
  );
}

const ORIGIN: Record<string, string> = { engine: "drafted in the engine", portal: "posted in the portal by hand, linked by a person", workbook: "imported from a registered workbook" };

// ─────────────────────────────────────────────────────────────── the tab

export function QuestionsPage() {
  const ws = useWs();
  const q = useQuestions(ws.id);
  const [params] = useSearchParams();
  const [status, setStatus] = useState(() => (QUESTION_FILTERS.some((f) => f.id === params.get("filter")) ? params.get("filter")! : "all"));
  const [batch, setBatch] = useState("all");
  const [area, setArea] = useState("all");
  const [unblocks, setUnblocks] = useState("all");
  const { open, drawer } = useWsAct(q.reload);
  const doc = q.parsed?.doc ?? null;
  const all = useMemo(() => doc?.questions ?? [], [doc]);
  const unblockOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of all) for (const r of unblockRefs(x)) m.set(r.ref, r.label);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [all]);
  if (q.error) return <ErrorState error={q.error} onRetry={q.reload} />;
  if (!q.data) return <LoadingRows rows={5} label="Loading the client questions" />;
  if (q.parsed?.error) return <ErrorState error={`The engine could not publish the client questions: ${q.parsed.error}`} onRetry={q.reload} />;
  const header = <PageHeader title="Client questions" subtitle="Every question to the client: its status in the portal, why it must be asked, and what its answer unblocks." />;
  if (!doc) return <div className="space-y-4">{header}<EmptyState title="The engine has not published the client questions yet.">They appear after its next publish of this engagement.</EmptyState></div>;
  const f = { status, batch, area, unblocks };
  const rows = filterQuestions(all, f);
  const count = (id: string) => filterQuestions(all, { ...f, status: id }).length;
  const states = Object.keys(QUESTION_STATE).map((s) => ({ s, n: all.filter((x) => x.status === s).length })).filter((x) => x.n > 0);
  const batches = [...new Set(all.map((x) => x.batch ?? "No batch"))].sort();
  const areas = [...new Set(all.map((x) => x.area ?? "General"))].sort();
  return (
    <div className="space-y-4">
      {header}
      <section aria-label="Questions by status" className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-semibold">{doc.total} question{doc.total === 1 ? "" : "s"}</span>
        <span className="text-xs text-muted">· {doc.counts.proven ?? 0} with the necessity proven ·</span>
        {states.map((x) => <QuestionStateCount key={x.s} state={x.s} count={x.n} />)}
      </section>
      {doc.portal.candidates.length > 0 && <PortalCandidates candidates={doc.portal.candidates} open={open} engagementId={ws.id} />}
      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Status" className="flex flex-wrap gap-1.5">
          {QUESTION_FILTERS.map((s) => (
            <button key={s.id} type="button" aria-pressed={status === s.id} onClick={() => setStatus(s.id)}
              className={`rounded-full border px-2.5 py-1 text-xs ${status === s.id ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line bg-raised hover:bg-sunken"}`}>
              {s.label} <span className="tabular-nums text-muted">{count(s.id)}</span>
            </button>
          ))}
        </div>
        <Select label="Batch" value={batch} onChange={setBatch} options={[["all", "All batches"], ...batches.map((b) => [b, b] as [string, string])]} />
        <Select label="Area" value={area} onChange={setArea} options={[["all", "All areas"], ...areas.map((a) => [a, a] as [string, string])]} />
        <Select label="Unblocks" value={unblocks} onChange={setUnblocks} options={[["all", "Anything"], ["none", "Nothing traced yet"], ...unblockOptions]} />
      </div>
      {rows.length === 0 ? <EmptyState title="No questions match.">Change the status, batch, area or unblocks filter.</EmptyState> : (
        <div className="overflow-x-auto rounded-lg border border-line bg-raised">
          <table className="w-full min-w-[760px] text-sm">
            <caption className="sr-only">Client questions</caption>
            <thead className="bg-sunken text-left text-xs text-muted"><tr>
              <th className="w-[38%] px-3 py-1.5 font-medium">Question</th><th className="px-3 py-1.5 font-medium">Batch</th><th className="px-3 py-1.5 font-medium">Area</th>
              <th className="w-44 px-3 py-1.5 font-medium">Status</th><th className="px-3 py-1.5 font-medium">Why we must ask</th><th className="px-3 py-1.5 font-medium">Unblocks</th>
            </tr></thead>
            <tbody className="divide-y divide-line">
              {rows.map((x) => <QuestionRow key={x.thread} q={x} engagementId={ws.id} />)}
            </tbody>
          </table>
        </div>
      )}
      {drawer}
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted">{label}
      <select aria-label={label} className="max-w-56 rounded-md border border-line bg-paper px-2 py-1 text-xs text-ink" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function QuestionRow({ q, engagementId }: { q: Question; engagementId: string }) {
  const u = unblockRefs(q);
  return (
    <tr className="align-top" data-thread={q.thread}>
      <td className="px-3 py-2"><Link to={questionHref(engagementId, q.thread)} className="font-medium text-brand hover:underline">{q.subject}</Link>
        <p className="line-clamp-2 text-xs text-muted">{q.text}</p>
        <p className="text-[11px] text-muted">{q.kind === "followup" ? "Follow-up" : "Question"} {q.thread}{q.portal ? " · in the portal" : ""}{q.origin !== "engine" ? ` · ${ORIGIN[q.origin] ?? q.origin}` : ""}</p></td>
      <td className="px-3 py-2 text-xs">{q.batch ?? <span className="text-muted">—</span>}</td>
      <td className="px-3 py-2 text-xs">{q.area ?? "General"}</td>
      <td className="px-3 py-2"><QuestionStatusChip question={q} />
        {(q.answers.length > 0 || q.sent_at) && <span className="mt-0.5 block text-[11px] text-muted">{q.answers.length ? `answered ${fmtDate(q.answers[q.answers.length - 1].at)}` : `sent ${fmtDate(q.sent_at)}`}</span>}</td>
      <td className="px-3 py-2 text-xs">{q.why.proven ? <StatusPill kind="done" label="Proven" /> : <StatusPill kind="waiting" label={`${q.why.gaps.length} gap${q.why.gaps.length === 1 ? "" : "s"}`} />}
        <span className="mt-0.5 block text-muted">{q.why.evidence.length} source{q.why.evidence.length === 1 ? "" : "s"}{q.scenarios.length ? ` · ${q.scenarios.length} scenario${q.scenarios.length === 1 ? "" : "s"}` : ""}</span></td>
      <td className="px-3 py-2 text-xs">{u.length === 0 ? <span className="text-muted">Nothing traced</span> : <>{u[0].label}{u.length > 1 && <span className="text-muted"> +{u.length - 1} more</span>}</>}</td>
    </tr>
  );
}

function PortalCandidates({ candidates, open, engagementId }: { candidates: PortalCandidate[]; open: (i: ActionIntent) => void; engagementId: string }) {
  return (
    <section aria-labelledby="portal-h" className="space-y-2 rounded-lg border border-waiting-line bg-waiting-bg px-4 py-3 text-sm">
      <h2 id="portal-h" className="font-semibold">Posted in the portal by hand — link them · {candidates.length}</h2>
      <p className="text-xs text-muted">These questions are in the client portal but were not sent through the engine. Nothing is linked until a person confirms: link each to the question it is, add it as a new one, or say it is not an audit question.</p>
      <ul className="space-y-2">
        {candidates.map((c) => (
          <li key={c.portal_id} className="rounded-md border border-line bg-raised px-3 py-2">
            <p className="font-medium">{c.title ?? "Untitled"}</p>
            <p className="whitespace-pre-wrap text-xs">{c.question}</p>
            <p className="text-[11px] text-muted">In the portal{c.created_at ? ` since ${fmtDate(c.created_at)}` : ""} · {c.status ?? "status unknown"}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {c.matches.map((m) => (
                <Button key={m.thread} size="sm" kind="primary" data-command="question.link-portal"
                  onClick={() => open({ command: "question.link-portal", engagementId, fixed: { portal: c.portal_id, thread: m.thread }, title: `Link to question ${m.thread}: ${m.subject}` })}>
                  Link to “{m.subject.slice(0, 48)}” ({m.strength})
                </Button>
              ))}
              {c.acts.create && <Button size="sm" data-command="question.link-portal" onClick={() => open({ command: "question.link-portal", engagementId, fixed: c.acts.create!.payload, title: `Add as a new question: ${c.title ?? c.question.slice(0, 40)}` })}>Add as a new question</Button>}
              {c.acts.dismiss && <Button size="sm" data-command="question.portal-dismiss" onClick={() => open({ command: "question.portal-dismiss", engagementId, fixed: c.acts.dismiss!.payload, title: `Not an audit question: ${c.title ?? c.question.slice(0, 40)}` })}>Not an audit question</Button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────── one question

export function QuestionPage() {
  const { thread: raw = "" } = useParams();
  const ws = useWs();
  const q = useQuestions(ws.id);
  const { open, drawer } = useWsAct(q.reload);
  const [decision, setDecision] = useState<"defer" | "skip" | "remove" | null>(null);
  if (q.error) return <ErrorState error={q.error} onRetry={q.reload} />;
  if (!q.data) return <LoadingRows rows={6} label="Loading the question" />;
  if (q.parsed?.error) return <ErrorState error={`The engine could not publish the client questions: ${q.parsed.error}`} onRetry={q.reload} />;
  const doc: QuestionsDoc | null = q.parsed?.doc ?? null;
  const back = <Link className="text-brand hover:underline" to={`/e/${ws.id}/questions`}>Client questions</Link>;
  const x = doc?.questions.find((y) => String(y.thread) === raw);
  if (!doc) return <EmptyState title="The engine has not published the client questions yet.">They appear after its next publish of this engagement. {back}</EmptyState>;
  if (!x) return <EmptyState title={`No client question ${raw} in this engagement.`}>{back}</EmptyState>;
  const act = (which: keyof Question["acts"], kind: "primary" | "secondary" = "secondary", title?: string) => {
    const a = x.acts[which];
    if (!a?.command) return null;
    return <Button key={which} size="sm" kind={kind} data-command={a.command}
      onClick={() => open({ command: a.command!, engagementId: ws.id, fixed: a.payload, title: `${title ?? a.label}: ${x.subject}` })}>{a.label}</Button>;
  };
  const decide = (d: "defer" | "skip" | "remove") => x.acts[d] && (
    <Button key={d} size="sm" data-command="question.decide" onClick={() => setDecision(d)}>{x.acts[d]!.label}</Button>
  );
  return (
    <article className="space-y-4" aria-labelledby="q-title">
      <PageHeader
        crumbs={<nav aria-label="Breadcrumb" className="text-xs text-muted">{back} › {x.kind === "followup" ? "Follow-up" : "Question"} {x.thread}</nav>}
        title={<span id="q-title">{x.subject}</span>}
        subtitle={<span className="flex flex-wrap items-center gap-2"><QuestionStatusChip question={x} />
          <span>{x.batch ?? "No batch"} · {x.area ?? "General"} · {x.topics.map((t) => t.label).join(", ")}{x.portal ? " · in the client portal" : ""}</span></span>}
        actions={<div className="flex flex-wrap gap-1.5">{act("send", "primary")}{act("accept", "primary")}{act("return")}{act("na")}{act("message")}{act("reword")}
          {decide("defer")}{decide("skip")}{decide("remove")}{act("restore", "primary")}{act("close")}{act("link")}</div>} />

      {x.decision && (
        <section aria-label="The auditor's decision" className="rounded-lg border border-neutral-line bg-neutral-bg px-4 py-3 text-sm">
          <p className="flex flex-wrap items-center gap-2"><QuestionStatusChip question={x} /><span className="font-semibold">{x.decision.state === "deferred" ? "Deferred" : x.decision.state === "skipped" ? "Not asked" : "Removed"}</span></p>
          <p className="mt-1 whitespace-pre-wrap">{x.decision.reason}</p>
          <p className="text-xs text-muted">by <Who name={x.decision.by} />{x.decision.agent ? ` (recorded by ${x.decision.agent} on their instruction)` : ""}{x.decision.at ? `, ${fmtStamp(x.decision.at)}` : ""}. Everything it would have unblocked is flagged until the work is done another way, it is restored, or a person records why it stands.</p>
        </section>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="What the client reads" className="min-w-0">
          <p className="whitespace-pre-wrap text-sm">{x.text}</p>
          <p className="mt-2 text-xs text-muted">{ORIGIN[x.origin] ?? x.origin}{x.workbook ? ` (${x.workbook.ref})` : ""}{x.due ? ` · due ${fmtDate(x.due)}` : ""}{x.portal ? ` · portal status: ${x.portal.status ?? "unknown"}` : ""}</p>
        </Card>
        <Card title="Where it stands" className="min-w-0">
          <ol className="space-y-1.5 text-sm" aria-label="Steps">
            {x.steps.map((s, n) => (
              <li key={n}><span className="font-medium capitalize">{s.step}</span> — {s.by ? <Who name={s.by} /> : <span className="text-muted">—</span>}{s.agent ? <span className="text-xs text-muted"> (with {s.agent})</span> : null}
                {s.at && <span className="text-xs text-muted">, {fmtStamp(s.at)}</span>}{s.portal && <span className="text-xs text-muted"> (in the portal)</span>}
                {(s.reason || s.how) && <span className="block text-xs text-muted">{s.reason ?? s.how}</span>}</li>
            ))}
          </ol>
        </Card>
      </div>

      <WhyWeMustAsk q={x} open={open} engagementId={ws.id} />
      <Unblocks q={x} engagementId={ws.id} />
      <Scenarios q={x} open={open} engagementId={ws.id} />

      <Card title={`The client's answers · ${x.answers.length}`}>
        {x.answers.length === 0 ? <p className="text-sm text-muted">No answer yet.</p> : (
          <ol className="space-y-2 text-sm">{x.answers.map((a) => (
            <li key={a.id} className="rounded-md border border-line px-3 py-2"><p className="text-[11px] text-muted">{a.author} · {fmtStamp(a.at)}{a.intent === "na_request" ? " · says it does not apply" : ""}</p>
              <p className="whitespace-pre-wrap">{a.body}</p><p className="text-[11px] text-muted">{a.statement}</p></li>
          ))}</ol>
        )}
      </Card>

      <Card title="History">
        <ol className="space-y-2 border-l border-line pl-4 text-sm" aria-label="History">
          {x.history.map((h, n) => (
            <li key={n}><p className="text-[11px] text-muted">{h.at ? fmtStamp(h.at) : "—"} · {h.by ? <Who name={h.by} /> : "—"}{h.agent ? ` (${h.agent})` : ""}{h.channel ? " · from the web app" : ""} · {h.kind.replace("register:", "")}</p>
              <p className="whitespace-pre-wrap">{h.text}</p>{h.instruction && <p className="text-xs text-muted">Instruction, word for word: “{h.instruction}”</p>}</li>
          ))}
        </ol>
      </Card>
      {decision && <DecisionDialog q={x} decision={decision} engagementId={ws.id} onClose={() => setDecision(null)} onSent={q.reload} />}
      {drawer}
    </article>
  );
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <section className="space-y-2"><div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-sm font-semibold">{title}</h3>{action}</div>{children}</section>;
}

function ProofBlock({ p, tone }: { p: QProof; tone: "accepted" | "proposed" }) {
  return (
    <div className={`space-y-1.5 rounded-md border px-3 py-2 text-sm ${tone === "proposed" ? "border-agent-line bg-agent-bg" : "border-line"}`}>
      <p aria-label="What we saw"><span className="font-medium">What we saw. </span><Words segs={p.saw} /></p>
      <p aria-label="The rule"><span className="font-medium">The rule. </span><Words segs={p.rule} /></p>
      <p aria-label="Why the auditor cares"><span className="font-medium">If it is not asked. </span><Words segs={p.risk} /></p>
      <p className="text-[11px] text-muted">{tone === "proposed"
        ? <>The agent's draft ({p.agent ?? p.drafted_by}, for <Who name={p.drafted_by} />, {fmtStamp(p.drafted_at)}) — waiting for a person to accept it.</>
        : <>Accepted by <Who name={p.accepted_by} />{p.accepted_at ? `, ${fmtStamp(p.accepted_at)}` : ""}{p.agent ? ` from the agent's draft (${p.agent})` : ""}; every figure is bound to its source.</>}</p>
    </div>
  );
}

function Facts({ facts, engagementId }: { facts: QFact[]; engagementId: string }) {
  if (!facts.length) return <p className="text-sm text-muted">No evidence is recorded yet.</p>;
  return (
    <ul className="space-y-2" aria-label="Evidence">
      {facts.map((f, n) => f.workbook ? (
        <li key={n} className={`rounded-md border px-3 py-2 text-sm ${f.ok ? "border-line" : "border-blocked-line bg-blocked-bg"}`}>
          <p><span className="font-medium">{f.label || f.describe}</span>{f.workbook.value_text && <>: <span className="font-mono">{f.workbook.value_text}</span></>}</p>
          <p className="text-xs text-muted">{f.workbook.workpaper}{f.workbook.title ? ` ${f.workbook.title}` : ""} · sheet {f.workbook.sheet}, cell {f.workbook.cell}{f.workbook.source ? ` · source: ${f.workbook.source}` : ""}</p>
          {f.workbook.formula && <p className="font-mono text-[11px] text-muted">{f.workbook.formula}</p>}
          {!f.ok && <p className="text-xs text-blocked">Does not resolve now: {f.why}</p>}
          <p className="mt-0.5 text-[11px] text-muted"><span className="font-mono">{f.cite}</span> · {f.via}</p>
        </li>
      ) : (
        <li key={n}><SourceList sources={[f]} engagementId={engagementId} /><p className="text-[11px] text-muted">{f.label && f.label !== f.describe ? `${f.label} · ` : ""}{f.via}</p></li>
      ))}
    </ul>
  );
}

function WhyWeMustAsk({ q, open, engagementId }: { q: Question; open: (i: ActionIntent) => void; engagementId: string }) {
  const w = q.why;
  const n = w.narrative;
  const ask = q.acts.explain;
  const accept = q.acts.explain_accept;
  return (
    <section aria-labelledby="why-h" className="space-y-4 rounded-lg border border-brand/40 bg-raised p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 id="why-h" className="text-base font-semibold">Why we must ask</h2>
          <p className="text-xs text-muted">The facts are computed from the record and carry their sources; the words are the preparer's, every figure bound to those sources.</p>
        </div>
        <span className="flex flex-wrap items-center gap-1.5">{w.proven ? <StatusPill kind="done" label="Necessity proven" /> : <StatusPill kind="waiting" label="Not yet proven" />}
          {accept?.command && <Button size="sm" kind="primary" data-command={accept.command} onClick={() => open({ command: accept.command!, engagementId, fixed: accept.payload, title: `Accept why we must ask: ${q.subject}` })}>{accept.label}</Button>}
          {ask?.command && <Button size="sm" data-command={ask.command} onClick={() => open({ command: ask.command!, engagementId, fixed: ask.payload, title: `${ask.label}: ${q.subject}` })}>{ask.label}</Button>}</span>
      </header>
      {w.gaps.length > 0 && <ul className="list-disc pl-5 text-xs text-waiting" aria-label="What the proof still lacks">{w.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>}
      {n.accepted && <ProofBlock p={n.accepted} tone="accepted" />}
      {n.proposed && <ProofBlock p={n.proposed} tone="proposed" />}
      <Section title="If it is not asked, this stays unaddressed">
        <ul className="space-y-1 text-sm" aria-label="Unaddressed if not asked">{w.unaddressed.map((u, i) => <li key={i}><span className="mr-1.5 rounded border border-line px-1 text-[10px] uppercase text-muted">{u.kind.replace("_", " ")}</span>{u.text}</li>)}</ul>
      </Section>
      <Section title="What directs the inquiry">
        {w.requires.length === 0 ? <p className="text-sm text-muted">No program step or procedure is recorded.</p> : (
          <ul className="space-y-1.5 text-sm">{w.requires.map((r, i) => <li key={i}>{r.text}{r.quote && <span className="block text-xs text-muted">“{r.quote}”</span>}</li>)}</ul>
        )}
      </Section>
      <Section title={`Evidence that raised it · ${w.evidence.length}`}><Facts facts={w.evidence} engagementId={engagementId} /></Section>
    </section>
  );
}

function Unblocks({ q, engagementId }: { q: Question; engagementId: string }) {
  const ws = useWs();
  const u = q.unblocks;
  const none = !u.issues.length && !u.workpapers.length && !u.procedures.length && !u.requests.length && !u.templates.length && !u.threads.length;
  const wpLink = (id: string, ref: string | null) => `/e/${engagementId}/wp/${encodeURIComponent(ref ?? ws.wp.find((w) => w.workpaperId === id)?.ref ?? id)}`;
  return (
    <Card title="What its answer unblocks">
      {none ? <p className="text-sm text-muted">Nothing is traced yet — link the issue, procedure or workpaper it serves.</p> : (
        <div className="grid gap-3 text-sm md:grid-cols-2">
          {u.issues.length > 0 && <div><h3 className="text-xs font-semibold text-muted">Issues</h3><ul className="mt-1 space-y-1">{u.issues.map((i) => (
            <li key={i.key} className="flex flex-wrap items-center gap-1.5"><Link className="text-brand hover:underline" to={issueHref(engagementId, i.key)}>{i.title}</Link>{i.chip && <IssueStatusChip chip={i.chip} />}<span className="block w-full text-[11px] text-muted">{i.via}</span></li>))}</ul></div>}
          {u.workpapers.length > 0 && <div><h3 className="text-xs font-semibold text-muted">Workpapers</h3><ul className="mt-1 space-y-1">{u.workpapers.map((w) => (
            <li key={w.id}><Link className="text-brand hover:underline" to={wpLink(w.id, w.ref)}>{w.ref ?? w.id} {w.name}</Link>{w.status_label && <span className="text-xs text-muted"> ({w.status_label})</span>}<span className="block text-[11px] text-muted">{w.via}</span></li>))}</ul></div>}
          {u.procedures.length > 0 && <div><h3 className="text-xs font-semibold text-muted">Procedures</h3><ul className="mt-1 space-y-1">{u.procedures.map((p) => (
            <li key={p.id}><span className="font-mono text-xs text-muted">{p.workpaper}{p.task ? ` ${p.task}` : ""}</span> {p.text} <span className="text-xs text-muted">({p.status})</span><span className="block text-[11px] text-muted">{p.via}</span></li>))}</ul></div>}
          {u.templates.length > 0 && <div><h3 className="text-xs font-semibold text-muted">Workpapers not opened yet</h3><ul className="mt-1 space-y-1">{u.templates.map((t) => <li key={t.template}>{t.label} {t.name}<span className="block text-[11px] text-muted">{t.via}</span></li>)}</ul></div>}
          {u.requests.length > 0 && <div><h3 className="text-xs font-semibold text-muted">Requests</h3><ul className="mt-1 space-y-1">{u.requests.map((r) => <li key={r.id}><Link className="text-brand hover:underline" to={`/e/${engagementId}/documents#pbc-${r.id}`}>PBC-{r.id} {r.text}</Link> <span className="text-xs text-muted">({r.status})</span></li>)}</ul></div>}
          {u.threads.length > 0 && <div><h3 className="text-xs font-semibold text-muted">Other client threads</h3><ul className="mt-1 space-y-1">{u.threads.map((t) => <li key={t.id}>{t.subject} <span className="text-xs text-muted">({t.kind}, {t.status})</span></li>)}</ul></div>}
        </div>
      )}
      {u.downstream.length > 0 && (
        <details className="mt-3 text-sm"><summary className="cursor-pointer text-xs font-medium text-muted">Downstream in the dependency graph · {u.downstream_total}</summary>
          <ul className="mt-1 space-y-0.5 text-xs">{u.downstream.map((d) => <li key={d.template}>{d.label} {d.name} <span className="text-muted">— {d.opened ? d.status_label : "not opened"}, via {d.via}</span></li>)}</ul></details>
      )}
      <div className="mt-3">
        <h3 className="text-xs font-semibold text-muted">Workpapers its answer may make necessary</h3>
        {q.may_create.length === 0 ? <p className="text-sm text-muted">None suggested.</p> : (
          <ul className="mt-1 space-y-1 text-sm" aria-label="May need to be created">{q.may_create.map((m) => (
            <li key={m.template}>{m.label} {m.name} <span className="text-xs text-muted">{m.opened ? "(already open)" : "(candidate)"} — {m.why}</span></li>))}</ul>
        )}
      </div>
    </Card>
  );
}

function Scenarios({ q, open, engagementId }: { q: Question; open: (i: ActionIntent) => void; engagementId: string }) {
  const ask = q.acts.scenarios;
  const match = q.acts.match;
  return (
    <Card title={`How the client might answer · ${q.scenarios.length}`}
      action={<span className="flex gap-1.5">{match?.command && <Button size="sm" kind="primary" data-command={match.command} onClick={() => open({ command: match.command!, engagementId, fixed: match.payload, title: `Mark the scenario that matched: ${q.subject}` })}>{match.label}</Button>}
        {ask?.command && <Button size="sm" data-command={ask.command} onClick={() => open({ command: ask.command!, engagementId, fixed: ask.payload, title: `${ask.label}: ${q.subject}` })}>{ask.label}</Button>}</span>}>
      {q.scenarios.length === 0 ? <p className="text-sm text-muted">No scenarios yet. Ask the agent for two to four ways the client might answer, each with our moves.</p> : (
        <ol className="space-y-3" aria-label="Answer scenarios">{q.scenarios.map((s, n) => <ScenarioItem key={s.id} s={s} n={n + 1} open={open} engagementId={engagementId} subject={q.subject} />)}</ol>
      )}
    </Card>
  );
}

function ScenarioItem({ s, n, open, engagementId, subject }: { s: QScenario; n: number; open: (i: ActionIntent) => void; engagementId: string; subject: string }) {
  const im = s.impact;
  return (
    <li data-scenario={s.id} className={`rounded-md border px-3 py-2 text-sm ${s.matched ? "border-done-line bg-done-bg" : s.status === "proposed" ? "border-agent-line bg-agent-bg" : "border-line"}`}>
      <p className="flex flex-wrap items-center gap-1.5"><span className="font-mono text-xs text-muted">{n}.</span>
        {s.matched ? <StatusPill kind="done" label="Matched the client's answer" /> : <StatusPill kind={s.status === "proposed" ? "waiting" : "neutral"} label={s.status === "proposed" ? "Proposed — waits for a person" : "Accepted"} />}</p>
      <p className="mt-1"><span className="font-medium">If the client says: </span><Words segs={s.answer} /></p>
      <ul className="mt-1 space-y-0.5" aria-label="Our moves">{s.moves.map((m, i) => (
        <li key={i}><span className="mr-1.5 rounded border border-line px-1 text-[10px] uppercase text-muted">{m.kind_label}</span><Words segs={m.text} />{m.consideration && <span className="ml-1 text-xs text-muted">(a consideration, not a conclusion)</span>}</li>))}</ul>
      {(im.issues.length > 0 || im.workpapers.length > 0 || im.procedures.length > 0 || im.templates.length > 0 || im.downstream.length > 0 || im.reaches.issues.length > 0) && (
        <div className="mt-1.5 text-xs text-muted">
          {im.reaches.issues.length > 0 && <p>Settles or moves: {im.reaches.issues.map((i) => i.title).join("; ")}</p>}
          {(im.templates.length > 0 || im.workpapers.length > 0) && <p>Opens or reaches: {[...im.workpapers.map((w) => `${w.ref ?? w.id} ${w.name}`), ...im.templates.map((t) => `${t.label} ${t.name}${t.opened ? "" : " (not opened yet)"}`)].join("; ")}</p>}
          {im.procedures.length > 0 && <p>Procedures: {im.procedures.map((p) => `${p.workpaper}${p.task ? ` ${p.task}` : ""}`).join("; ")}</p>}
          {im.downstream.length > 0 && <p>Then downstream: {im.downstream.slice(0, 6).map((d) => `${d.label}`).join(", ")}{im.downstream.length > 6 ? " …" : ""}</p>}
        </div>
      )}
      <p className="mt-1 text-[11px] text-muted">{s.agent ? `Drafted by ${s.agent} for ` : "By "}<Who name={s.by} />, {fmtStamp(s.at)}{s.decided_by ? <> · accepted by <Who name={s.decided_by} /></> : ""}{s.matched_by ? <> · matched by <Who name={s.matched_by} />{s.match_note ? `: ${s.match_note}` : ""}</> : ""}</p>
      {s.status === "proposed" && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Button size="sm" kind="primary" data-command="question.scenario-decide" onClick={() => open({ command: "question.scenario-decide", engagementId, fixed: { scenario: s.id, decision: "accept" }, title: `Accept scenario ${n}: ${subject}` })}>Accept</Button>
          <Button size="sm" data-command="question.scenario-decide" onClick={() => open({ command: "question.scenario-decide", engagementId, fixed: { scenario: s.id, decision: "reject" }, title: `Reject scenario ${n}: ${subject}` })}>Reject</Button>
        </div>
      )}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────── defer / skip / remove — a person's act, with the proof shown first

const DECISION_WORDS: Record<string, { title: string; verb: string; what: string }> = {
  defer: { title: "Defer this question", verb: "Defer", what: "It stays a draft and is not sent now." },
  skip: { title: "Skip — don't ask this question", verb: "Skip it", what: "It is not asked; if it was sent, it is closed for the firm." },
  remove: { title: "Remove this question", verb: "Remove it", what: "It leaves the register's open list; it can be restored." },
};

export function DecisionDialog({ q, decision, engagementId, onClose, onSent }: { q: Question; decision: "defer" | "skip" | "remove"; engagementId: string; onClose: () => void; onSent?: () => void }) {
  const { catalogue, me } = useApp();
  const request = useEngine();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const words = DECISION_WORDS[decision];
  const allowed = mayRequest(catalogue.get("question.decide"), me, "engagement");
  const short = reason.trim().length < 15;
  return (
    <Drawer open onClose={onClose} title={words.title}
      footer={<div className="flex flex-wrap items-center justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button kind="primary" data-command="question.decide" disabled={!allowed.ok || short || busy} title={!allowed.ok ? allowed.reason ?? undefined : short ? "Say why (at least 15 characters)" : undefined}
          onClick={async () => {
            setBusy(true);
            const row = await request({ command: "question.decide", engagementId, payload: { thread: q.thread, decision, reason: reason.trim() }, what: `${words.verb}: ${q.subject}`, idempotencyKey: key });
            setBusy(false);
            if (row) { onSent?.(); onClose(); }
          }}>{words.verb}</Button>
      </div>}>
      <div className="space-y-3 text-sm" data-decision={decision}>
        <p className="flex flex-wrap items-center gap-2"><QuestionStatusChip question={q} /><span className="font-medium">{q.subject}</span></p>
        <p className="whitespace-pre-wrap text-xs text-muted">{q.text}</p>
        <p>{words.what} This is your decision: it is logged with your reason and what you saw below.</p>
        <section aria-label="Why we must ask" className="space-y-1.5 rounded-md border border-line px-3 py-2">
          <p className="flex items-center gap-1.5 font-semibold">Why we must ask {q.why.proven ? <StatusPill kind="done" label="Necessity proven" /> : <StatusPill kind="waiting" label="Not yet proven" />}</p>
          {q.why.narrative.accepted ? <p><Words segs={q.why.narrative.accepted.risk} /></p> : <p className="text-xs text-muted">No accepted explanation in words yet.</p>}
          <p className="text-xs text-muted">{q.why.evidence.length} source{q.why.evidence.length === 1 ? "" : "s"} raised it{q.why.requires.filter((r) => r.kind !== "standard").length ? `; ${q.why.requires.filter((r) => r.kind !== "standard").map((r) => r.text).join("; ")}` : ""}.</p>
        </section>
        <section aria-label="What stays unaddressed" className="space-y-1 rounded-md border border-waiting-line bg-waiting-bg px-3 py-2">
          <p className="font-semibold">If it is not asked, this stays unaddressed</p>
          <ul className="list-disc space-y-0.5 pl-5 text-xs">{q.why.unaddressed.map((u, i) => <li key={i}>{u.text}</li>)}</ul>
          <p className="text-xs text-muted">Each item it would have unblocked is flagged until the work is done another way, the question is restored, or a person records why it stands.</p>
        </section>
        <label className="block text-sm font-medium" htmlFor="decision-reason">Your reason</label>
        <textarea id="decision-reason" className="block min-h-24 w-full rounded-md border border-line bg-paper px-2.5 py-1.5 text-sm" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="For example: the client answered this in the intake questionnaire (section 7)." />
        {!allowed.ok && <p role="alert" className="text-xs text-blocked">{allowed.reason}</p>}
      </div>
    </Drawer>
  );
}
